import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import * as enTranslation from './locales/en.json';
import * as frTranslation from './locales/fr.json';
import * as roTranslation from './locales/ro.json';
import * as zhTranslation from './locales/zh.json';
import * as esTranslation from './locales/es.json';
import * as deTranslation from './locales/de.json';
import * as ptBrTranslation from './locales/pt-br.json';
import * as jaTranslation from './locales/ja.json';
import * as koTranslation from './locales/ko.json';
import * as itTranslation from './locales/it.json';
import * as nlTranslation from './locales/nl.json';

export const SUPPORTED_LANGUAGES = [
  'en',
  'fr',
  'ro',
  'zh',
  'es',
  'de',
  'pt-br',
  'ja',
  'ko',
  'it',
  'nl',
] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const LOCALE_PREFERENCE_STORAGE_KEY = 'assetron.locale';

const isSupportedLanguage = (
  language: string | null | undefined,
): language is SupportedLanguage => {
  return Boolean(
    language && SUPPORTED_LANGUAGES.includes(language as SupportedLanguage),
  );
};

const normalizeLanguage = (
  language: string | string[] | null | undefined,
): SupportedLanguage | null => {
  if (!language) return null;
  const value = Array.isArray(language) ? language[0] : language;
  if (!value) return null;
  const normalized = value.toLowerCase().replace('_', '-');
  if (isSupportedLanguage(normalized)) {
    return normalized;
  }
  const baseLanguage = normalized.split('-')[0];
  return isSupportedLanguage(baseLanguage) ? baseLanguage : null;
};

const getProfileLanguagePreference = (): SupportedLanguage | null => {
  if (typeof window === 'undefined') return null;

  const profileLanguage = (
    window as Window & Record<string, string | undefined>
  )['__ASSETRON_PROFILE_LOCALE__']; // eslint-disable-line dot-notation -- global key includes underscores by design
  return normalizeLanguage(profileLanguage);
};

const getRuntimeLanguagePreference = (): SupportedLanguage | null => {
  if (typeof navigator === 'undefined') return null;

  const preferredLanguages =
    Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];

  for (const language of preferredLanguages) {
    const normalized = normalizeLanguage(language);
    if (normalized) {
      return normalized;
    }
  }

  return null;
};

export const getPersistedLanguagePreference = (): SupportedLanguage | null => {
  if (typeof localStorage === 'undefined') return null;

  try {
    return normalizeLanguage(
      localStorage.getItem(LOCALE_PREFERENCE_STORAGE_KEY),
    );
  } catch {
    return null;
  }
};

export const setPersistedLanguagePreference = (
  language: string,
): SupportedLanguage | null => {
  const normalizedLanguage = normalizeLanguage(language);
  if (!normalizedLanguage || typeof localStorage === 'undefined') {
    return null;
  }

  try {
    localStorage.setItem(LOCALE_PREFERENCE_STORAGE_KEY, normalizedLanguage);
  } catch {
    return null;
  }

  return normalizedLanguage;
};
// creating a language detection plugin using browser and i18next detection inputs
// http://i18next.com/docs/ownplugin/#languagedetector
const languageDetector = {
  type: 'languageDetector',
  async: true, // flags below detection to be async
  detect: (callback: (arg0: string | string[] | undefined) => void) => {
    const detector = new LanguageDetector();

    try {
      const profileLanguage = getProfileLanguagePreference();
      if (profileLanguage) {
        callback(profileLanguage);
        return profileLanguage;
      }

      const persistedLanguage = getPersistedLanguagePreference();
      if (persistedLanguage) {
        callback(persistedLanguage);
        return persistedLanguage;
      }

      const runtimeLanguage = getRuntimeLanguagePreference();
      if (runtimeLanguage) {
        callback(runtimeLanguage);
        return runtimeLanguage;
      }

      const detected = detector.detect();
      const normalized = normalizeLanguage(detected) ?? 'en';
      callback(normalized);
      return normalized;
    } catch {
      callback('en'); // Fallback to 'en' in case of an error
      return 'en';
    }
  },
  init: () => {},
  cacheUserLanguage: (language: string) => {
    setPersistedLanguagePreference(language);
  },
};

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    compatibilityJSON: 'v3',
    resources: {
      en: {
        common: enTranslation,
      },
      fr: {
        common: frTranslation,
      },
      ro: {
        common: roTranslation,
      },
      zh: {
        common: zhTranslation,
      },
      es: {
        common: esTranslation,
      },
      de: {
        common: deTranslation,
      },
      'pt-br': {
        common: ptBrTranslation,
      },
      ja: {
        common: jaTranslation,
      },
      ko: {
        common: koTranslation,
      },
      it: {
        common: itTranslation,
      },
      nl: {
        common: nlTranslation,
      },
    },

    // have a common namespace used around the full app
    ns: ['common'],
    defaultNS: 'common',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    nonExplicitSupportedLngs: true,

    debug: false,

    // cache: {
    //   enabled: true
    // },

    interpolation: {
      escapeValue: false, // not needed for react as it does escape per default to prevent xss!
    },

    react: {
      useSuspense: false, // Prevent react-i18next from throwing Promises into React Suspense
    },
  });
export default i18n;
